import { create } from "zustand";
import type {
  ActiveProposal,
  ApprovalFlowAbilities,
  OpenedProposalRequest,
} from "@services/types/assignment-proposal/assignmentProposalTypes";

export type ApprovalFlowAbilitiesStatus = "idle" | "loading" | "loaded" | "error";

interface State {
  /** Approval-flow permissions of the logged user. Null until loaded or when the flow is off. */
  abilities: ApprovalFlowAbilities | null;
  abilitiesStatus: ApprovalFlowAbilitiesStatus;
  /** Counts the retries of the abilities request: the loader asks again on every change. */
  abilitiesAttempt: number;
  /**
   * The proposal the assignation editor is bound to: the one it was opened for, or the one saved
   * or sent in this editor session. Kept in memory only and cleared when the editor is left.
   */
  activeProposal: ActiveProposal | null;
  /** Load of the proposal named by the URL. Present from the moment it is requested. */
  openedProposalRequest: OpenedProposalRequest | null;
  /**
   * The page the editor was opened from (`returnTo` of the router state), kept for the whole visit:
   * the form wipes the router state once it has applied what the URL asked for.
   */
  editorReturnTo: string | null;
}

const initialState: State = {
  abilities: null,
  abilitiesStatus: "idle",
  abilitiesAttempt: 0,
  activeProposal: null,
  openedProposalRequest: null,
  editorReturnTo: null,
};

export const useAssignmentProposalStore = create<State>(() => ({ ...initialState }));

/** Abilities and status in one write, so a reader never sees abilities of a previous load. */
export const setApprovalFlowAbilitiesState = (
  abilities: ApprovalFlowAbilities | null,
  abilitiesStatus: ApprovalFlowAbilitiesStatus,
) => {
  useAssignmentProposalStore.setState({ abilities, abilitiesStatus });
};

/** Asks the abilities again after a failed request, wherever their loader is mounted. */
export const retryApprovalFlowAbilities = () => {
  useAssignmentProposalStore.setState(state => ({ abilitiesAttempt: state.abilitiesAttempt + 1 }));
};

export const setActiveProposal = (activeProposal: ActiveProposal | null) => {
  useAssignmentProposalStore.setState({ activeProposal });
};

export const setOpenedProposalRequest = (openedProposalRequest: OpenedProposalRequest | null) => {
  useAssignmentProposalStore.setState({ openedProposalRequest });
};

export const setEditorReturnTo = (editorReturnTo: string | null) => {
  useAssignmentProposalStore.setState({ editorReturnTo });
};

/** Forgets the proposal of the editor (active and requested) without touching the abilities. */
export const resetEditorProposal = () => {
  useAssignmentProposalStore.setState({ activeProposal: null, openedProposalRequest: null });
};

/** Test-only: runtime code must call resetEditorProposal, never wipe the loaded abilities. */
export const resetAssignmentProposalStore = () => {
  useAssignmentProposalStore.setState({ ...initialState });
};
